import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import clinicService from '../../../services/clinic.service';
import articlesService from '../../../services/articles.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import Loading from '../../common/Loading';
import './ArticlePrint.css';

function ArticlePrint() {
    const { articleId } = useParams();
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const [article, setArticle] = useState(null);
    const [clinic, setClinic] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [articleNotFound, setArticleNotFound] = useState(false);
    const hasPrinted = useRef(false);

    useEffect(() => {
        if (!clinicId || !articleId) {
            setIsLoading(false);
            return;
        }

        let isMounted = true;

        const loadPrintData = async () => {
            setIsLoading(true);
            setArticleNotFound(false);

            try {
                const [articleResponse, clinicResponse] = await Promise.all([
                    articlesService.getArticleById(clinicId, articleId),
                    clinicService.getClinicInfo(clinicId),
                ]);

                if (!isMounted) {
                    return;
                }

                setArticle(articleResponse.data);
                setClinic(clinicResponse.data);
            } catch {
                if (isMounted) {
                    setArticleNotFound(true);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadPrintData();

        return () => {
            isMounted = false;
        };
    }, [articleId, clinicId]);

    useEffect(() => {
        if (isLoading || articleNotFound || !article || hasPrinted.current) {
            return;
        }

        hasPrinted.current = true;
        const timer = window.setTimeout(() => {
            window.print();
        }, 400);

        return () => window.clearTimeout(timer);
    }, [isLoading, article, articleNotFound]);

    return (
        <div className="article-print-shell">
            <div className="article-print-toolbar article-print-no-print">
                <button type="button" className="btn btn-light-primary btn-sm rounded-pill" onClick={() => window.print()}>
                    <i className="bi bi-printer me-1"></i>
                    Print
                </button>
                <button type="button" className="btn btn-light btn-sm rounded-pill" onClick={() => window.close()}>
                    <i className="bi bi-x-lg me-1"></i>
                    Close
                </button>
            </div>

            <div className="article-print-page">
                {isLoading ? (
                    <div className="article-print-loading article-print-no-print">
                        <Loading />
                    </div>
                ) : articleNotFound ? (
                    <div className="article-print-empty">
                        <h1>Article Not Found</h1>
                    </div>
                ) : (
                    <>
                        {(clinic?.logo || clinic?.name) && (
                            <header className="article-print-header">
                                {clinic?.logo && (
                                    <img src={clinic.logo} alt={clinic.name || 'Clinic logo'} className="article-print-logo" />
                                )}
                                {clinic?.name && <p className="article-print-clinic-name mb-0">{clinic.name}</p>}
                            </header>
                        )}

                        <article className="article-print-article">
                            <h1 className="article-print-title">{article.title}</h1>

                            <div
                                className="article-print-content"
                                dangerouslySetInnerHTML={{ __html: article.content }}
                            />
                        </article>
                    </>
                )}
            </div>
        </div>
    );
}

export default ArticlePrint;
